import { computed, observable, runInAction } from "mobx"
import { toast } from "sonner"

import { Model } from "@/lib/Model.ts"

import type { CodingGuidelinesModel as ICodingGuidelinesModel } from "@/components/CodingGuidelinesDialog/CodingGuidelinesModel.ts"
import type { TID } from "@/lib/client/generated/TypeDefinitions.ts"
import type { Queries } from "@/lib/queries.ts"


export class TaskGuidelinesModel extends Model implements ICodingGuidelinesModel {
  readonly initialGuidelines = observable.box("")
  readonly guidelines = observable.box("")
  readonly readonly = observable.box(false)

  readonly hasChanges = computed(() => this.guidelines.get() !== this.initialGuidelines.get())
  readonly isSaving = observable.box(false)

  readonly handleSave = () => {
    if (this.readonly.get()) {
      toast.error("Task guidelines are read-only")
      return
    }
    runInAction(() => {
      this.isSaving.set(true)
    })
    try {
      void this.queries.apiClient.updateTaskGuidelines(
        this.taskId,
        this.guidelines.get()
      ).then(() => {
        runInAction(() => this.initialGuidelines.set(this.guidelines.get()))
        toast.success("Guidelines updated")
      })
    } finally {
      runInAction(() => {
        this.isSaving.set(false)
      })
    }
  }

  readonly handleDiscard = () => {
    runInAction(() => this.guidelines.set(this.initialGuidelines.get()))
  }

  readonly load = async () => {
    runInAction(() => this.isLoading.set(true))

    try {
      const content = await this.queries.getTaskGuidelines(this.taskId)

      runInAction(() => {
        const value = content?.content ?? ""
        this.initialGuidelines.set(value)
        this.guidelines.set(value)
        this.readonly.set(content?.readonly ?? false)
      })
    } finally {
      runInAction(() => this.isLoading.set(false))
    }
  }

  constructor(private readonly taskId: TID, readonly queries: Queries) {
    super("TaskGuidelinesModel")

    void this.load()
  }
}
